import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Column, FormField, InputTextField, LoadingButton, Row, Typography,
} from '@tempmee/react';
import { useUpdateLocationUser } from '../../../../domain/Users';
import { showSuccessMessage } from '../../../../utils';
import { EDITABLE_ACCOUNT_FIELDS, locationUserProfilePropType } from '../constants';
import { changedFields } from '../utils';

const toDraft = (user) =>
  Object.fromEntries(EDITABLE_ACCOUNT_FIELDS.map(({ key }) => [key, user[key] ?? '']));

/**
 * Edits the identity's name, email and phone in auth-service.
 *
 * Only the fields that changed are sent (see `changedFields`). A refused save
 * keeps the form open with what was typed; the reason reaches the admin through
 * the global mutation toaster, verbatim from auth-service. A successful one
 * closes the form once the profile has refetched, so the card shows what was
 * stored — auth-service normalises the phone number.
 */
export const AccountInfoForm = ({ user, onClose }) => {
  const [draft, setDraft] = useState(() => toDraft(user));
  const [hasTriedSave, setHasTriedSave] = useState(false);
  const update = useUpdateLocationUser(user.id);

  const { changes, errors } = changedFields(user, draft);
  const hasErrors = Object.keys(errors).length > 0;
  const isUnchanged = !hasErrors && Object.keys(changes).length === 0;

  const handleSave = () => {
    setHasTriedSave(true);
    if (hasErrors) return;

    update.mutate(changes, {
      onSuccess: () => {
        showSuccessMessage({ message: 'Location user has been updated' });
        onClose();
      },
    });
  };

  return (
    <Column gap={3}>
      {EDITABLE_ACCOUNT_FIELDS.map(({ key, label, id }) => {
        const error = hasTriedSave ? errors[key] : undefined;
        const errorId = `${id}-error`;

        return (
          <FormField key={key} label={label} htmlFor={id}>
            <InputTextField
              id={id}
              value={draft[key]}
              onChange={(event) => {
                const { value } = event.target;
                setDraft((current) => ({ ...current, [key]: value }));
              }}
              isError={Boolean(error)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            />
            {error ? (
              <Typography id={errorId} role="alert" variant="body2" className="text-error">
                {error}
              </Typography>
            ) : null}
          </FormField>
        );
      })}

      <Row gap={2} justify="end">
        <Button variant="text" color="secondary" onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
        <LoadingButton loading={update.isPending} disabled={isUnchanged} onClick={handleSave}>
          Save
        </LoadingButton>
      </Row>
    </Column>
  );
};

AccountInfoForm.propTypes = {
  user: locationUserProfilePropType.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AccountInfoForm;
